import { AsyncPipe } from '@angular/common';
import { AfterViewInit, ChangeDetectionStrategy, Component, DOCUMENT, Inject, OnDestroy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { map, Observable } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { PageUtils } from '../../../common/page.utils';
import { AssessmentPanelUtils } from '../../../common/ui.utils';
import { TestletType } from '../../../constants/testlet-type.enum';
import { APP_CONFIG, AppConfig } from '../../../models/app-config';
import { AssessmentAttemptedStatus } from '../../../models/assessment-models';
import { AssessmentPlayerService } from '../../../services/player.service';
import { PageContentState } from '../../../store/models/assessment-models';
import { selectCurrentUpdateAssessmentState } from '../../../store/selectors/assessment.selectors';
import { selectActionButtons, selectDisplayActionButtons } from '../../../store/selectors/page-content.selectors';
import { selectLoadingState } from '../../../store/selectors/page-loading.selectors';
import { LearnosityPlayerComponent } from '../../common/learnosity-player/learnosity-player.component';
import { PageComponent } from '../../common/page/page.component';
import { PageConfig } from '../../common/page/page.models';
import { TestletActionButtonsConfig } from '../../common/testlet-action-buttons/models/testlet-action-buttons.models';
import { TestletActionButtonsComponent } from '../../common/testlet-action-buttons/testlet-action-buttons.component';
import { BaseTestletHandler } from '../base-testlet-handler';

@Component({
  selector: 'objects',
  templateUrl: './objects.component.html',
  styleUrls: ['./objects.component.scss', '../styles/testlet.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [AsyncPipe, PageComponent, LearnosityPlayerComponent, TestletActionButtonsComponent],
})
export class ObjectsComponent extends BaseTestletHandler implements OnDestroy, AfterViewInit {
  protected updatedAssessmentConfig = this.store.select(selectCurrentUpdateAssessmentState);
  protected displayActionButtons$: Observable<boolean> = this.store.select(selectDisplayActionButtons);
  protected testletActionButtonsConfig$: Observable<TestletActionButtonsConfig> = this.store
    .select(selectActionButtons)
    .pipe(map((buttons) => ({ buttonTrackingContext: 'objects', ...buttons })));
  protected pageConfig$: Observable<PageConfig> = this.store
    .select(selectLoadingState)
    .pipe(map((state) => ({ loading: state.loadingData.loading })));

  constructor(
    @Inject(DOCUMENT) private document: Document,
    protected playerService: AssessmentPlayerService,
    protected store: Store<PageContentState>,
    private pageUtils: PageUtils,
    @Inject(APP_CONFIG) private readonly config: AppConfig,
  ) {
    super(playerService, store);
  }

  get buttonId() {
    return `objects-submit-button-${this.currentQuestionIndex}`;
  }

  ngAfterViewInit() {
    // Set Event Listeners action methods here.
    this.eventHandlers = {
      questionLoadEventNotifier$: (itemPosition) => {
        this.setQuestionPageControls(itemPosition);
        this.insertSubmitButton();
      },
      questionChangeEventNotifier$: (response) => this.toggleButtonState(response.attemptedStatus),
    };
    this.updatedAssessmentConfig.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((updateAssessment) => {
      if (
        updateAssessment != null &&
        !updateAssessment.loading &&
        updateAssessment.error == null &&
        updateAssessment.data?.config != null &&
        updateAssessment.data?.nextTestlet == 'Objects'
      ) {
        const testlet = updateAssessment.data;
        this.initializeListeners(
          JSON.parse(updateAssessment.data.config),
          'Objects',
          testlet.sessionId,
          testlet.assessmentId,
        );
      } else {
        this.pageUtils.navigateToIntroductionPage(!updateAssessment?.loading);
      }
    });

    this.buttonClickSubject
      .pipe(debounceTime(this.config.debounceTime), takeUntilDestroyed(this.destroyRef))
      .subscribe((type) => {
        this.handleDebouncedButtonClick(type);
      });
  }

  ngOnDestroy() {
    this.onDestroy();
  }

  private toggleButtonState(attemptStatus: string) {
    const button = this.document.getElementById(this.buttonId) as HTMLButtonElement;
    button.disabled = attemptStatus === AssessmentAttemptedStatus.NotAttempted;
  }

  /**
   * @description insert a custom submit button
   */
  private insertSubmitButton() {
    const submitBtn = AssessmentPanelUtils.createElement(this.document, 'button', 'submit', this.buttonId);

    AssessmentPanelUtils.setElementAttribute(
      submitBtn,
      'btn custom-submit-btn text-capitalize text-white border-0 rounded-pill mt-4 py-2 font-size-18 mx-auto',
      'submit button',
      '0',
      true,
    );
    this.setButtonClickListener(submitBtn);

    const submitContainer = AssessmentPanelUtils.createContainerElement(
      this.document,
      'div',
      [submitBtn],
      'submit-btn-container',
    );

    const responseArea = this.document.getElementsByClassName('lrn_mcqgroup');
    responseArea[this.currentQuestionIndex].insertAdjacentElement('afterend', submitContainer);
  }

  /**
   * @description Attach click event handler to the an HTML button element.
   * @param buttonElement - Indicates the button to which click event handler is to be added.
   */
  private setButtonClickListener(buttonElement: HTMLElement) {
    buttonElement?.addEventListener('click', () => {
      this.navigateToNextItem();
    });
  }

  protected readonly TestletType = TestletType;
}
